import * as RadixTooltip from '@radix-ui/react-tooltip'
import { ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * Material-Design tooltip wrapper around Radix UI Tooltip.
 *
 * Pass `<TooltipProvider>` once at the app root with the hover timing (already
 * done in main.tsx). Then use `<Tooltip content="...">` to wrap any element
 * that should show a tooltip on hover/focus.
 *
 * Defaults:
 *  - #289: 100 ms show delay, instant within 300 ms of another (src/lib/hoverIntent.ts)
 *  - #289: the hover cards' motion (.hover-motion), theme-aware popover background
 *  - arrow pointing at the trigger
 *  - dismisses on cursor leave / blur / escape
 *
 * The trigger element receives the tooltip via Radix's `asChild` pattern, so
 * the trigger keeps its own DOM tag and event handlers intact.
 */

export const TooltipProvider = RadixTooltip.Provider

interface TooltipProps {
  /** The element the tooltip describes. Will be rendered as the trigger via Radix asChild. */
  children: ReactNode
  /** Tooltip body. Plain string or any ReactNode (icons, multi-line, bold, etc.). */
  content: ReactNode
  /** Side the tooltip prefers (Radix auto-flips on viewport edge). Default 'top'. */
  side?: 'top' | 'right' | 'bottom' | 'left'
  /** Tooltip alignment relative to the trigger. Default 'center'. */
  align?: 'start' | 'center' | 'end'
  /** Override the global show delay for this tooltip. */
  delayDuration?: number
  /** Disable the tooltip (e.g., when content is empty). Default false. */
  disabled?: boolean
  /** Extra Tailwind classes for the content panel. */
  className?: string
}

export function Tooltip({
  children,
  content,
  side = 'top',
  align = 'center',
  delayDuration,
  disabled = false,
  className,
}: TooltipProps) {
  if (disabled || content === null || content === undefined || content === '') {
    return <>{children}</>
  }
  return (
    <RadixTooltip.Root delayDuration={delayDuration}>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          side={side}
          align={align}
          sideOffset={6}
          className={cn(
            'z-50 max-w-[280px] rounded-md border bg-popover px-2.5 py-1.5 text-xs text-popover-foreground shadow-md',
            'hover-motion',   // #289 — the hover cards' motion: 120 ms fade + 4 px slide, none when warm
            className,
          )}
        >
          {content}
          <RadixTooltip.Arrow className="fill-popover" width={10} height={5} />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  )
}
